import { addDays, startOfDay } from "date-fns";
import { messageForUser } from "@/utils/errorMapping";
import { codigoDelError, estadoDelError } from "./errores";
import { datosDeCore } from "./respuestaDeCore";

/**
 * Las observaciones de las ventas de la caja (trd-client#346 AC-4; §2.8 y
 * §2.10 de OFFLINE-POS-RONDA-2.md). Core nunca rechaza una venta de la caja
 * por dominio: lo que no cierra queda como observación, y el dueño la revisa.
 * Los textos son los mismos en la caja y en la pantalla del dueño.
 */

const TEXTOS: Record<string, string> = {
  // Ronda 1 (§2.2 de OFFLINE-POS-RONDA-1.md).
  PRICE_MISMATCH: "Se cobró un precio distinto del vigente",
  OVERSELLING: "Sin stock: la salida se registró igual",
  CASH_DESK_CLOSED: "Se vendió con la caja cerrada",
  DUPLICATE_TERMINAL: "Otro equipo subió una venta con el mismo número",
  ORDER_RULE_BROKEN: "No cumplió una regla de la orden",
  // Ronda 2: la venta sin red, contra el mandato (§2.5).
  PRICE_OFF_SHEET: "Se cobró distinto de la hoja de precios de la caja",
  OUTSIDE_WINDOW: "Se vendió sin conexión fuera del plazo",
  PAYMENT_METHOD_NOT_ALLOWED: "Se cobró con un medio que no vale sin conexión",
  LIMIT_EXCEEDED: "Pasó el tope de venta sin conexión",
  PRODUCT_INACTIVE: "Se vendió un producto que ya no estaba activo",
};

/** En palabras del mostrador. Un código que no está acá queda como viene. */
export function textoDeLaObservacion(codigo: string): string {
  return TEXTOS[codigo] ?? codigo;
}

export type EstadoDeLaObservacion = "open" | "reviewed";
export type Resolucion = "ok" | "return" | "adjustment";

/** `ObservationRow` (§2.8). */
export interface ObservacionDeLaCaja {
  id: string;
  saleId: string;
  orderId: string | null;
  terminalId: string;
  code: string;
  detail: string;
  occurredAt: string;
  receivedAt: string;
  status: EstadoDeLaObservacion;
  resolution: Resolucion | null;
  note: string | null;
  resolvedBy: string | null;
  resolvedAt: string | null;
}

export interface PaginaDeObservaciones {
  items: ObservacionDeLaCaja[];
  /** `null` en la última página. */
  nextCursor: string | null;
}

/**
 * Una página de la cola. El contrato no dice cómo viene la página: se lee como
 * las de la foto (§2.1), `{ items, nextCursor }`, y también una lista sola.
 */
export function paginaDeObservaciones(cuerpo: unknown): PaginaDeObservaciones {
  const datos = datosDeCore(cuerpo) as
    | { items?: unknown; nextCursor?: unknown }
    | unknown[]
    | null
    | undefined;
  const filas = Array.isArray(datos) ? datos : datos?.items;
  const cursor = Array.isArray(datos) ? null : datos?.nextCursor;
  return {
    items: Array.isArray(filas)
      ? (filas as ObservacionDeLaCaja[]).filter(
          (fila) => typeof fila?.id === "string" && fila.id !== ""
        )
      : [],
    nextCursor: typeof cursor === "string" && cursor !== "" ? cursor : null,
  };
}

export const RESOLUCIONES: { valor: Resolucion; texto: string }[] = [
  { valor: "ok", texto: "Está bien así" },
  { valor: "return", texto: "Se hizo una devolución" },
  { valor: "adjustment", texto: "Se hizo un ajuste" },
];

export function textoDeLaResolucion(resolucion: Resolucion | null): string {
  return RESOLUCIONES.find((r) => r.valor === resolucion)?.texto ?? "Revisada";
}

/**
 * Sin fechas, core trae sólo los últimos 30 días, y una observación abierta
 * más vieja no se vería: la pantalla del dueño diría que no hay nada para
 * revisar (#359). Sin `desde`, se pide desde siempre.
 */
export const DESDE_SIEMPRE = new Date(0).toISOString();

export interface FiltrosDeObservaciones {
  estado: EstadoDeLaObservacion | "todas";
  warehouseId: string | null;
  /**
   * Los días en que llegaron al servidor (`receivedAt`), del calendario de
   * quien mira, los dos inclusive. Sin fechas, todas.
   */
  desde: Date | null;
  hasta: Date | null;
}

/**
 * La query de `GET /pos/observations` (§2.8): sólo lo que filtra. Core filtra
 * `receivedAt` en `[from, to)`: `to` es el principio del día siguiente.
 */
export function consultaDeObservaciones(
  filtros: FiltrosDeObservaciones,
  cursor: string | null
): Record<string, string> {
  const consulta: Record<string, string> = {};
  if (filtros.estado !== "todas") consulta.status = filtros.estado;
  if (filtros.warehouseId) consulta.warehouseId = filtros.warehouseId;
  consulta.from = filtros.desde
    ? startOfDay(filtros.desde).toISOString()
    : DESDE_SIEMPRE;
  if (filtros.hasta) {
    consulta.to = startOfDay(addDays(filtros.hasta, 1)).toISOString();
  }
  if (cursor) consulta.cursor = cursor;
  return consulta;
}

/** El cuerpo de `POST /pos/observations/{id}/resolve`: la nota, si hay. */
export function pedidoDeResolucion(
  resolucion: Resolucion,
  nota: string
): { resolution: Resolucion; note?: string } {
  const limpia = nota.trim();
  return limpia
    ? { resolution: resolucion, note: limpia }
    : { resolution: resolucion };
}

export function motivoAlResolver(error: unknown): string {
  if (codigoDelError(error) === "business_suspended") {
    return "El negocio está suspendido: no se pueden resolver observaciones.";
  }
  if (estadoDelError(error) === 403) {
    return "Para resolver observaciones hace falta el permiso de Depósitos.";
  }
  return messageForUser(error, "No se pudo guardar la resolución.");
}
